/**
 * Integer division with an explicit rounding rule. Every money computation that can produce
 * a fraction of a minor unit (VAT, percentages, ratios) goes through here — there is no
 * floating point anywhere in `@wato/money`.
 *
 * - `half_up` (DEFAULT): round half away from zero ("commercial rounding"). Used for VAT and
 *   every amount printed on an invoice, matching the usual ZATCA/NBR tax-invoice practice
 *   of rounding each amount to the currency's minor unit, halves away from zero.
 * - `half_even`: banker's rounding (half to the nearest even). For aggregate statistics /
 *   estimates where many roundings must not drift in one direction.
 * - `floor` / `ceil`: toward −∞ / +∞ (e.g. "never charge more than" / "at least").
 * - `trunc`: toward zero.
 */
export const ROUNDING_MODES = ["half_up", "half_even", "floor", "ceil", "trunc"] as const;
export type RoundingMode = (typeof ROUNDING_MODES)[number];

export const DEFAULT_ROUNDING: RoundingMode = "half_up";

/** `numerator / denominator` rounded to an integer with `mode`. `denominator` must be ≠ 0. */
export function divideRounded(
  numerator: bigint,
  denominator: bigint,
  mode: RoundingMode = DEFAULT_ROUNDING,
): bigint {
  if (denominator === 0n) throw new RangeError("division by zero");
  // Normalize so the denominator is positive; the sign lives in the numerator.
  const n = denominator < 0n ? -numerator : numerator;
  const d = denominator < 0n ? -denominator : denominator;
  const q = n / d; // BigInt division truncates toward zero
  const r = n - q * d; // same sign as n (or 0)
  if (r === 0n) return q;
  const negative = n < 0n;
  const twiceRemainder = (r < 0n ? -r : r) * 2n;
  switch (mode) {
    case "trunc":
      return q;
    case "floor":
      return negative ? q - 1n : q;
    case "ceil":
      return negative ? q : q + 1n;
    case "half_up":
      if (twiceRemainder >= d) return negative ? q - 1n : q + 1n;
      return q;
    case "half_even": {
      if (twiceRemainder > d) return negative ? q - 1n : q + 1n;
      if (twiceRemainder < d) return q;
      // Exactly half: pick the even neighbour.
      const away = negative ? q - 1n : q + 1n;
      return q % 2n === 0n ? q : away;
    }
    default: {
      const exhaustive: never = mode;
      throw new RangeError(`unknown rounding mode ${String(exhaustive)}`);
    }
  }
}
